import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Modal, Tabs, Input, Checkbox, Select, Button, message } from 'antd'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import {
    CopyCommentContextModalProps,
    TabKey,
} from './CopyCommentContextModal.types'
import styles from './CopyCommentContextModal.module.less'
import { commentResponseTemplateAtomFamily } from '@/atoms/commentResponseTemplate'
import {
    setCommentResponseTemplate,
    resetCommentResponseTemplate,
} from '@/api/cb/commentResponseTemplate'
import {
    substituteTemplateVariables,
    getAvailableVariablesForContentType,
} from './TemplateVariableSubstitution'
import { notecardAtomFamily } from '@/atoms/notecard'
import { practiceProblemAtomFamily } from '@/atoms/practiceProblem'
import { slimLeafNodeAtomFamily } from '@/atoms/slimLeafNode'
import { hashContentPath } from 'common/src/ContentPath'
import { PracticeProblemType } from 'common/src/practiceProblems/consts'
import { IMultiPartProblemBase } from 'common/src/practiceProblems/types'
import { ContentType } from 'common/src/commentThread/types'
import { getSourceMaterial } from '@/api/cb/contentCreatorContent'
import { tryExtractTextFromStringifiedEditorState } from 'magic-text-box-lexical/src/utils/extractTextFromStringifiedEditorState'
import { commentResponseTemplateInvalidationAtomFamily } from '@/atoms/commentResponseTemplate'
import { invalidate } from '@/atoms/types'

const { TextArea } = Input
const { TabPane } = Tabs

export const CopyCommentContextModal: React.FC<
    CopyCommentContextModalProps
> = ({
    commentMetadata,
    contentType,
    contentId,
    contentPath,
    courseName,
    commentThreadSelection,
    isVisible,
    onClose,
}) => {
    // State
    const [activeTab, setActiveTab] = useState<TabKey>('edit')
    const [localTemplate, setLocalTemplate] = useState<string>('')
    const [includeSourceMaterial, setIncludeSourceMaterial] =
        useState<boolean>(false)
    const [selectedSubProblemIndex, setSelectedSubProblemIndex] = useState<
        number | null
    >(null)
    const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true)
    const [sourceMaterialText, setSourceMaterialText] = useState<string>('')

    // Recoil atoms
    const template = useRecoilValue(
        commentResponseTemplateAtomFamily(contentType)
    )
    const setTemplateInvalidation = useSetRecoilState(
        commentResponseTemplateInvalidationAtomFamily(contentType)
    )

    // Content data atoms - must call hooks unconditionally
    const notecardData = useRecoilValue(notecardAtomFamily(contentId))
    const practiceProblemData = useRecoilValue(
        practiceProblemAtomFamily(contentId)
    )
    const slimLeafNodeData = useRecoilValue(
        slimLeafNodeAtomFamily(hashContentPath(contentPath))
    )

    // Select the appropriate data based on content type
    const notecard =
        contentType === ContentType.NOTECARD ? notecardData : undefined
    const practiceProblem =
        contentType === ContentType.PRACTICE_PROBLEM
            ? practiceProblemData
            : undefined
    const slimLeafNode =
        contentType === ContentType.OUTLINE ||
        contentType === ContentType.SUMMARY
            ? slimLeafNodeData
            : undefined

    // Initialize local template when template changes
    useEffect(() => {
        setLocalTemplate(template)
    }, [template])

    // Load source materials
    useEffect(() => {
        const loadSourceMaterials = async () => {
            if (
                !slimLeafNode?.sourceMaterialIDs ||
                slimLeafNode.sourceMaterialIDs.length === 0
            ) {
                setSourceMaterialText('')
                return
            }

            try {
                const sourceMaterials = await Promise.all(
                    slimLeafNode.sourceMaterialIDs.map((id) =>
                        getSourceMaterial({
                            id,
                            courseName,
                            contentPath,
                        })
                    )
                )

                const concatenatedText = sourceMaterials
                    .map((response) => {
                        const material = response.data.payload
                        const title = material.title || 'Source Material'
                        // Source material content is already Markdown text, not magic text box JSON
                        const content = material.content || ''
                        return `${title}:\n${content}`
                    })
                    .join('\n\n')

                setSourceMaterialText(concatenatedText)
            } catch (error) {
                console.error('Failed to load source materials:', error)
                message.warning('Could not load source material')
            }
        }

        if (isVisible) {
            loadSourceMaterials()
            setIsLoadingContent(false)
        }
    }, [isVisible, slimLeafNode, courseName])

    // Extract comment text
    const commentText = useMemo(() => {
        return tryExtractTextFromStringifiedEditorState(commentMetadata.text)
    }, [commentMetadata.text])

    // Check if multi-part problem
    const isMultiPartProblem = useMemo(() => {
        return (
            practiceProblem?.type === PracticeProblemType.MULTI_PART &&
            (practiceProblem as IMultiPartProblemBase).subProblems.length > 0
        )
    }, [practiceProblem])

    // Get the appropriate outline content based on content type
    const outlineContent = useMemo(() => {
        if (!slimLeafNode) return undefined
        return contentType === ContentType.SUMMARY
            ? slimLeafNode.summary
            : slimLeafNode.outline
    }, [slimLeafNode, contentType])

    // Generate preview
    const previewText = useMemo(() => {
        return substituteTemplateVariables({
            template: localTemplate,
            contentType,
            commentText,
            includeSourceMaterial,
            sourceMaterialText,
            notecardData: notecard,
            practiceProblemData: practiceProblem,
            outlineContent,
            outlineSelection: commentThreadSelection,
            selectedSubProblemIndex,
        })
    }, [
        localTemplate,
        contentType,
        commentText,
        includeSourceMaterial,
        sourceMaterialText,
        notecard,
        practiceProblem,
        outlineContent,
        commentThreadSelection,
        selectedSubProblemIndex,
    ])

    // Available variables
    const availableVariables = useMemo(() => {
        return getAvailableVariablesForContentType(contentType)
    }, [contentType])

    // Handlers
    const handleSaveTemplate = useCallback(async () => {
        try {
            await setCommentResponseTemplate(contentType, localTemplate)
            setTemplateInvalidation((prev) => invalidate(prev))
            message.success('Template saved successfully')
        } catch (error) {
            console.error('Failed to save template:', error)
            message.error('Failed to save template')
        }
    }, [contentType, localTemplate, setTemplateInvalidation])

    const handleResetTemplate = useCallback(async () => {
        try {
            const response = await resetCommentResponseTemplate(contentType)
            const defaultTemplate = response.data.payload.template
            setLocalTemplate(defaultTemplate)
            setTemplateInvalidation((prev) => invalidate(prev))
            message.success('Template reset to default')
        } catch (error) {
            console.error('Failed to reset template:', error)
            message.error('Failed to reset template')
        }
    }, [contentType, setTemplateInvalidation])

    const handleCopyToClipboard = useCallback(() => {
        navigator.clipboard
            .writeText(previewText)
            .then(() => {
                message.success({
                    content: 'Successfully copied context to clipboard',
                    duration: 3,
                    key: 'copy-comment-context',
                })
                onClose()
            })
            .catch((error) => {
                console.error('Failed to copy to clipboard:', error)
                message.error({
                    content: 'Failed to copy to clipboard',
                    duration: 3,
                })
            })
    }, [previewText, onClose])

    // Multi-part problem dropdown options
    const subProblemOptions = useMemo(() => {
        if (!isMultiPartProblem) return []

        const options = [
            { value: null, label: 'Entire Problem' },
            ...(practiceProblem as IMultiPartProblemBase).subProblems.map(
                (_, index) => ({
                    value: index,
                    label: `Part ${String.fromCharCode(65 + index)}`,
                })
            ),
        ]

        return options
    }, [isMultiPartProblem, practiceProblem])

    return (
        <Modal
            title={<div className="modal-title">Copy Context to Clipboard</div>}
            open={isVisible}
            onCancel={onClose}
            width={800}
            footer={
                <div className={styles.footer}>
                    <Button onClick={handleResetTemplate}>
                        Reset to Default
                    </Button>
                    <div className={styles.rightButtons}>
                        <Button onClick={handleSaveTemplate}>
                            Save Template
                        </Button>
                        <Button type="primary" onClick={handleCopyToClipboard}>
                            Copy to Clipboard
                        </Button>
                    </div>
                </div>
            }
        >
            <div className={styles.modalContent}>
                {/* Source Material Checkbox */}
                {sourceMaterialText && (
                    <Checkbox
                        checked={includeSourceMaterial}
                        onChange={(e) =>
                            setIncludeSourceMaterial(e.target.checked)
                        }
                        className={styles.checkbox}
                    >
                        Include source material
                    </Checkbox>
                )}

                {/* Multi-Part Problem Dropdown */}
                {isMultiPartProblem && (
                    <div className={styles.dropdown}>
                        <label>Problem Selection:</label>
                        <Select
                            value={selectedSubProblemIndex}
                            onChange={setSelectedSubProblemIndex}
                            options={subProblemOptions}
                            style={{ width: 200, marginLeft: '0.5rem' }}
                        />
                    </div>
                )}

                {/* Tabs */}
                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => setActiveTab(key as TabKey)}
                >
                    <TabPane tab="Edit" key="edit">
                        <div className={styles.editTab}>
                            <div className={styles.hints}>
                                <strong>Available variables:</strong>{' '}
                                {availableVariables
                                    .map((v) => `{{${v}}}`)
                                    .join(', ')}
                            </div>
                            <TextArea
                                value={localTemplate}
                                onChange={(e) =>
                                    setLocalTemplate(e.target.value)
                                }
                                rows={15}
                                className={styles.templateEditor}
                            />
                        </div>
                    </TabPane>
                    <TabPane tab="Preview" key="preview">
                        <div className={styles.previewTab}>
                            <pre className={styles.previewContent}>
                                {previewText}
                            </pre>
                        </div>
                    </TabPane>
                </Tabs>
            </div>
        </Modal>
    )
}
